import { useState, useEffect } from "react";
import fleetApi, { fleetError } from "@/lib/fleetApi";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Loader2 } from "lucide-react";

const STATUSES = ["open", "investigating", "resolved", "closed"];
const darkField = "w-full rounded-md border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-blue-600";

export default function IncidentStatusDialog({ incident, open, onOpenChange, onUpdated }) {
  const [status, setStatus] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && incident) { setStatus(incident.status || "open"); setNote(""); }
  }, [open, incident]);

  const submit = async () => {
    if (!status) { toast.error("Select a status."); return; }
    setSaving(true);
    try {
      await fleetApi.post(`/incidents/${incident.id}/status?status=${encodeURIComponent(status)}`, { note: note.trim() });
      toast.success(`Incident marked ${status}.`);
      onUpdated?.(incident.id, { status });
      onOpenChange(false);
    } catch (e) {
      const code = e.response?.status;
      toast.error(code === 403 ? "You don't have permission to update incidents."
        : fleetError(e, "Update failed."));
    } finally { setSaving(false); }
  };

  if (!incident) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="border-slate-800 bg-[#0f172a] text-white" data-testid="incident-status-dialog">
        <DialogHeader>
          <DialogTitle>Update incident status</DialogTitle>
          <p className="text-sm text-slate-400">{incident.type} · {incident.rider_name || incident.driver_name || "—"}</p>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-400">Status</label>
            <select value={status} onChange={(e) => setStatus(e.target.value)} className={darkField} data-testid="incident-status-select">
              {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-400">Note (optional)</label>
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3}
              placeholder="Add context for this status change…" data-testid="incident-status-note"
              className="border-slate-700 bg-slate-900 text-white placeholder:text-slate-500" />
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="text-slate-300 hover:bg-slate-800 hover:text-white" data-testid="incident-status-cancel">Cancel</Button>
          <Button onClick={submit} disabled={saving} className="gap-2 bg-blue-700 text-white hover:bg-blue-800" data-testid="incident-status-confirm">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
